import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  isDebtorType1,
  setCasePlaceOrderUpdateFields,
  setCaseForJointCaseOrderUpdateFields
} from 'reducers/activecase/activecaseSlice'

import { useMutation } from '@apollo/client'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  PLACE_COURSE_ORDER_LOOKUP,
  PlaceCourseOrderLookupVariables,
  PlaceCourseOrderLookupResponse
} from 'share/mutations/courses'
import {
  HasPlacedCourseOrderStatusType,
  CourseOrderStep,
  getCasePlaceOrderUpdateFields
} from 'share/domain/case'
import { CourseType } from 'share/domain/course'

import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  ErrorMessage,
  Formik,
  Form,
  FastField,
  FormikHelpers,
  FormikProps
} from 'formik'
import * as Yup from 'yup'
import { Button } from 'react-bootstrap'
import { ssnRegExp } from 'share/utils/utils'
import { SsnInput } from 'components/molecules/SsnInput'

interface UserVerificationFormValues {
  lastName: string
  ssn: string
}

const CourseTabPlaceOrderUserVerificationForm = (): JSX.Element => {
  const isDebtor1 = useSelector(isDebtorType1)
  const dispatch = useAppDispatch()
  const authentication = useAppSelector((state) => state.authentication)
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const [submitError, setSubmitError] = useState('')

  const [placeCourseOrderLookup, { loading }] = useMutation<
    PlaceCourseOrderLookupResponse,
    PlaceCourseOrderLookupVariables
  >(PLACE_COURSE_ORDER_LOOKUP)

  const initialValues: UserVerificationFormValues = {
    lastName:
      authentication.account && authentication.account.familyName
        ? authentication.account.familyName
        : '',
    ssn: ''
  }

  async function placeCourseOrderLookupHelper(
    formValues: UserVerificationFormValues
  ): Promise<boolean> {
    let result = false

    const errorCasePlaceOrderUpdateFields = getCasePlaceOrderUpdateFields(
      isDebtor1,
      activeCourse?.activeTab === 'first',
      HasPlacedCourseOrderStatusType.ErrorOnLookupStepUserVerification,
      CourseOrderStep.CourseOrderFailed
    )

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType:
          activeCourse?.activeTab === 'first'
            ? CourseType.CreditCounseling
            : CourseType.DebtorEducation,
        lastName: formValues.lastName,
        ssn: formValues.ssn,
        isDebtor1: isDebtor1
      }

      const lookupResult = await placeCourseOrderLookup({
        variables: { request: input }
      })

      if (!lookupResult.data?.orders.placeCourseOrderLookup.success) {
        setSubmitError('Error occurred while looking up order')
        console.log('error 1')
        console.dir(errorCasePlaceOrderUpdateFields)
        dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
        result = false
      } else {
        result = true
        dispatch(
          setCasePlaceOrderUpdateFields(
            lookupResult.data?.orders.placeCourseOrderLookup
          )
        )
        dispatch(
          setCaseForJointCaseOrderUpdateFields(
            lookupResult.data?.orders.placeCourseOrderLookup
          )
        )
      }
    } catch (e) {
      setSubmitError('Error occurred while looking up order')
      result = false
      console.log('error 2')
      console.dir(errorCasePlaceOrderUpdateFields)
      dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
    }

    return result
  }

  const loadingDelay = useLoadingDelay(loading)

  const validationSchema = Yup.object({
    lastName: Yup.string().required('Last Name is required'),
    ssn: Yup.string()
      .matches(ssnRegExp, 'Ssn is not valid')
      .required('SSN is Required')
  })

  function onSubmitWrapper(
    formik: FormikProps<UserVerificationFormValues>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    formik.handleSubmit(e)
  }

  async function onSubmit(
    values: UserVerificationFormValues,
    { setSubmitting }: FormikHelpers<UserVerificationFormValues>
  ) {
    placeCourseOrderLookupHelper(values)
    setSubmitting(false)
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={(e) => onSubmitWrapper(formik, e)}>
            <div
              id='userVerificationDetails'
              data-testid='user-verification-form'
            >
              <div className='row'>
                <div className='form-group col-6'>
                  <label htmlFor='LastName'>Your LastName</label>
                  <FastField
                    readOnly={true}
                    autoFocus
                    className='form-control'
                    id='lastName'
                    name='lastName'
                    data-testid='course-order-your-last-name-field'
                    placeholder=''
                  />
                  <ErrorMessage
                    name='lastName'
                    render={(msg) => (
                      <span
                        className='text-danger'
                        data-testid='course-order-your-last-name-field-error'
                      >
                        {msg}
                      </span>
                    )}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-6'>
                  <label htmlFor='ssn'>Your SSN</label>
                  <FastField
                    autoFocus
                    className={
                      formik.errors.ssn
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    id='ssn'
                    name='ssn'
                    as={SsnInput}
                    data-testid='course-order-your-ssn-field'
                    placeholder=''
                  />
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='form-group col-6'>
                <Button
                  type='submit'
                  variant='primary'
                  data-testid='course-order-submit-button'
                >
                  Next
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      {submitError && <div>{submitError}</div>}
    </>
  )
}

export default CourseTabPlaceOrderUserVerificationForm
